/**
 * React Query hooks for the Board Governance screens.
 *
 * Query keys mirror the legacy dashboard hook (useBoardQuery.js) verbatim
 * so invalidation semantics match the original:
 *   meetings  → ['board', 'meetings']
 *   meeting   → ['board', 'meetings', id]
 *   draft     → ['board', 'meetings', id, 'draft']
 *   members   → ['board', 'members']
 *
 * Each mutation invalidates the SAME keys the source invalidated:
 *   - create  → meetings
 *   - cancel  → meetings + meeting(id)
 *   - start   → meeting(id)
 *   - end     → meeting(id) + draft(id)
 *   - publish → meeting(id) + meetings
 *   - updateDraft → draft(id)
 *   - minutes / recording / addItem → meeting(id)
 *   - attendance / vote / conflict  → meeting(id)
 *
 * staleTimes carried over from source: meetings 30s, meeting 5s,
 * members 60s, draft 5s.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addVotingItem,
  AddVotingItemInput,
  cancelMeeting,
  castVote,
  CastVoteInput,
  createMeeting,
  CreateMeetingInput,
  declareConflict,
  endMeeting,
  fetchBoardMembers,
  fetchMeeting,
  fetchMeetings,
  fetchResultsDraft,
  markAttendance,
  publishResults,
  startMeeting,
  submitRecording,
  updateMinutes,
  updateResultsDraft,
  UpdateResultsDraftInput,
  AttendanceStatus
} from '@/api/board';

const KEYS = {
  meetings: ['board', 'meetings'] as const,
  meeting: (id: string) => ['board', 'meetings', id] as const,
  draft: (id: string) => ['board', 'meetings', id, 'draft'] as const,
  members: ['board', 'members'] as const
};

// ─── Reads ─────────────────────────────────────────────────────────────────────

export function useBoardMeetings() {
  return useQuery({
    queryKey: KEYS.meetings,
    queryFn: fetchMeetings,
    staleTime: 30_000
  });
}

export function useBoardMeeting(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.meeting(id ?? ''),
    queryFn: () => fetchMeeting(id!),
    enabled: !!id,
    staleTime: 5_000
  });
}

export function useBoardMembers() {
  return useQuery({
    queryKey: KEYS.members,
    queryFn: fetchBoardMembers,
    staleTime: 60_000
  });
}

export function useResultsDraft(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.draft(id ?? ''),
    queryFn: () => fetchResultsDraft(id!),
    enabled: !!id,
    staleTime: 5_000
  });
}

// ─── Mutations — meeting lifecycle ──────────────────────────────────────────────

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMeetingInput) => createMeeting(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meetings });
    }
  });
}

export function useCancelMeeting(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelMeeting(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meetings });
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
    }
  });
}

export function useStartMeeting(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => startMeeting(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
    }
  });
}

export function useEndMeeting(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => endMeeting(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
      qc.invalidateQueries({ queryKey: KEYS.draft(id) });
    }
  });
}

export function usePublishResults(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publishResults(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
      qc.invalidateQueries({ queryKey: KEYS.meetings });
    }
  });
}

export function useUpdateResultsDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateResultsDraftInput) =>
      updateResultsDraft(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.draft(id) });
    }
  });
}

export function useUpdateMinutes(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (minutes: string) => updateMinutes(id, minutes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
    }
  });
}

export function useSubmitRecording(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recordingUrl: string) => submitRecording(id, recordingUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
    }
  });
}

export function useAddVotingItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddVotingItemInput) => addVotingItem(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
    }
  });
}

// ─── Mutations — board member self-service ──────────────────────────────────────

export function useMarkAttendance(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: AttendanceStatus) => markAttendance(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(id) });
    }
  });
}

export function useCastVote(meetingId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CastVoteInput) =>
      castVote(meetingId, itemId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(meetingId) });
    }
  });
}

export function useDeclareConflict(meetingId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => declareConflict(meetingId, itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.meeting(meetingId) });
    }
  });
}
